import { useMemo } from 'react';
import type { EventFieldPath } from '@/types/field-mapping-flow';

/**
 * Recursively read a JSON object and extract all leaf-node paths
 * in JSONPath notation (e.g., $.customer.email).
 *
 * Mirrors the original app's jsonpath_readObject() function.
 */
function extractFieldPaths(
  obj: unknown,
  currentPath: string,
  paths: EventFieldPath[],
  question?: string
): void {
  if (obj === null || obj === undefined) {
    paths.push({ path: currentPath, sampleValue: obj, label: questionLabel(currentPath, question) });
    return;
  }

  if (typeof obj !== 'object') {
    // Leaf node (string, number, boolean)
    paths.push({ path: currentPath, sampleValue: obj, label: questionLabel(currentPath, question) });
    return;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      paths.push({ path: currentPath, sampleValue: '[]' });
      return;
    }
    // For arrays, index into the first element to show available paths
    obj.forEach((item, index) => {
      extractFieldPaths(item, `${currentPath}[${index}]`, paths, question);
    });
    return;
  }

  // Object — recurse into properties
  const keys = Object.keys(obj as Record<string, unknown>);
  if (keys.length === 0) {
    paths.push({ path: currentPath, sampleValue: '{}' });
    return;
  }

  // Calendly questions_and_answers items all have the same keys (question /
  // answer / question_uuid), so tag their fields with the question text.
  const ownQuestion = (obj as Record<string, unknown>).question;
  const itemQuestion = typeof ownQuestion === 'string' && ownQuestion.trim() ? ownQuestion.trim() : question;

  for (const key of keys) {
    const value = (obj as Record<string, unknown>)[key];
    extractFieldPaths(value, `${currentPath}.${key}`, paths, itemQuestion);
  }
}

/** "Address" + "$.x[1].answer" → "Address - Answer"; undefined when there is no question */
function questionLabel(path: string, question?: string): string | undefined {
  if (!question) return undefined;
  const leafKey = path.split('.').pop() || path;
  const humanLeaf = leafKey
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${question} - ${humanLeaf}`;
}

/**
 * Hook that parses a raw event JSON object into a list of field paths
 * with sample values, ready for use in the sidebar fields panel.
 */
export function useEventFieldPaths(eventData: Record<string, unknown> | null): EventFieldPath[] {
  return useMemo(() => {
    if (!eventData) return [];

    const paths: EventFieldPath[] = [];
    extractFieldPaths(eventData, '$', paths);
    return paths.sort((a, b) => a.path.localeCompare(b.path));
  }, [eventData]);
}
